/**
 * Jurisdiction Name Canonicalization Utility (frontend)
 * =======================================================
 * Mirror of /app/backend/utils/jurisdiction_canonical.py.
 * Keep both files in sync.
 *
 * Canonicalization rules (applied in order):
 *   1. Uppercase + trim
 *   2. Strip apostrophes, periods, and commas (replaced with spaces)
 *   3. Collapse internal whitespace
 *   4. Strip leading "CITY OF " / "TOWN OF " / "TOWNSHIP OF " /
 *      "VILLAGE OF " / "BOROUGH OF " / "COUNTY OF " / "PARISH OF " /
 *      "MUNICIPALITY OF "
 *   5. Strip trailing TYPE suffix tokens (repeat to handle doubles).
 *      Types stripped: SCHOOL DISTRICT, SCHOOL DIST, COUNTY, PARISH,
 *      BOROUGH, CITY, TOWNSHIP, TWP, TOWN, VILLAGE, MUNICIPALITY,
 *      SCHOOL. Feb 2026 also strips a single generic modifier
 *      immediately before the type suffix: CHARTER, AREA, UNIFIED,
 *      INDEPENDENT, CONSOLIDATED, REGIONAL, CENTRAL.
 *   6. Expand abbreviations: ST -> SAINT, STE -> SAINTE, MT -> MOUNT, FT -> FORT
 *   7. Merge "MC X" -> "MCX"  (McKean / Mc Kean / MCKEAN all -> MCKEAN)
 *   8. Collapse whitespace
 *
 * Examples:
 *   "St. Mary's County", "ST MARYS COUNTY", "Saint Marys County" → "SAINT MARYS"
 *   "McKean County", "Mc Kean County", "MCKEAN"                 → "MCKEAN"
 */

const TYPE_SUFFIXES = [
  // Multi-word suffixes MUST come first — JS regex alternation is
  // left-to-right, so "SCHOOL DISTRICT" must beat the shorter
  // "SCHOOL" alone.
  'SCHOOL DISTRICT',
  'SCHOOL DIST',
  'COUNTY',
  'PARISH',
  'BOROUGH',
  'CITY',
  'TOWNSHIP',
  'TWP',
  'TOWN',
  'VILLAGE',
  'MUNICIPALITY',
  'SCHOOL',
];

// Feb 2026 — Generic modifier tokens that sit immediately before a
// type suffix and carry NO identity information. Stripped only when
// followed by a real type suffix (see SUFFIX_RE), so a genuine
// identity word ("PORT CHARTER" without a suffix) is left untouched.
// See backend/utils/jurisdiction_canonical.py for the full rationale.
const MODIFIER_TOKENS = [
  'CHARTER',
  'AREA',
  'UNIFIED',
  'INDEPENDENT',
  'CONSOLIDATED',
  'REGIONAL',
  'CENTRAL',
];

const SUFFIX_RE = new RegExp(
  `\\s+(?:(?:${MODIFIER_TOKENS.join('|')})\\s+)?`
  + `(?:${TYPE_SUFFIXES.join('|')})$`,
);
const PREFIX_RE = /^(?:CITY OF|TOWN OF|TOWNSHIP OF|VILLAGE OF|BOROUGH OF|COUNTY OF|PARISH OF|MUNICIPALITY OF)\s+/;
const MC_RE = /\bMC\s+([A-Z])/g;
// Particle prefixes that should be space-merged with the following token
// (De Soto → DESOTO, La Salle → LASALLE, Van Buren → VANBUREN, etc.).
const PARTICLE_PREFIXES = ['DE', 'LA', 'LE', 'EL', 'LOS', 'VAN', 'VON', 'DU', 'DA', 'DI'];
const PARTICLE_RE = new RegExp(`\\b(${PARTICLE_PREFIXES.join('|')})\\s+([A-Z])`, 'g');
const WHITESPACE_RE = /\s+/g;

const ABBREV_EXPANSIONS = {
  ST: 'SAINT',
  STE: 'SAINTE',
  MT: 'MOUNT',
  FT: 'FORT',
};

/**
 * Return the canonical form of a jurisdiction name. Returns "" for falsy input.
 * Idempotent — safe to call on already-canonical strings.
 *
 * @param {string|null|undefined} name
 * @returns {string}
 */
export const canonicalizeJurisdictionName = (name) => {
  if (!name) return '';
  let s = String(name).toUpperCase().trim();
  if (!s) return '';

  // 2. Strip punctuation (replace with space)
  s = s.replace(/['\u2019]/g, '');           // apostrophes (straight + curly)
  s = s.replace(/[.,/\-]/g, ' ');

  // 3. Collapse whitespace
  s = s.replace(WHITESPACE_RE, ' ').trim();

  // 4. Strip leading prefixes
  s = s.replace(PREFIX_RE, '').trim();

  // 5. Strip trailing TYPE suffix (loop to handle doubles)
  let prev = null;
  while (prev !== s) {
    prev = s;
    s = s.replace(SUFFIX_RE, '').trim();
  }

  // 6. Expand abbreviations token-by-token
  s = s
    .split(' ')
    .map((tok) => ABBREV_EXPANSIONS[tok] || tok)
    .join(' ');

  // 7. Merge "MC X" -> "MCX"
  s = s.replace(MC_RE, 'MC$1');

  // 7b. Merge particle prefixes with the following word (DE SOTO → DESOTO,
  //     LA SALLE → LASALLE, VAN BUREN → VANBUREN, etc.). Run twice so
  //     chained particles like "DE LA" collapse cleanly.
  for (let i = 0; i < 2; i += 1) {
    s = s.replace(PARTICLE_RE, '$1$2');
  }

  // 8. Final whitespace collapse
  s = s.replace(WHITESPACE_RE, ' ').trim();

  return s;
};

/**
 * Build a canonical 3-tuple key for exact-equality lookup.
 * Returns "" if any component is missing.
 */
export const canonicalJurisdictionKey = (state, jurisdictionType, name) => {
  const cs = (state || '').toString().toUpperCase().trim();
  const ct = (jurisdictionType || '').toString().toLowerCase().trim();
  const cn = canonicalizeJurisdictionName(name);
  if (!cs || !ct || !cn) return '';
  return `${cs}|${ct}|${cn}`;
};
